import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import {
  useLayoutEffect,
  useState,
  type ReactNode,
  type RefObject,
} from "react";

interface FloatingPanelShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerRef?: RefObject<HTMLElement | null>;
  title: string;
  children: ReactNode;
  panelWidthPx?: number;
  panelHeightPx?: number;
  offsetX?: number;
  offsetY?: number;
}

export const FloatingPanelShell = ({
  open,
  onOpenChange,
  triggerRef,
  title,
  children,
  panelWidthPx = 200,
  panelHeightPx = 310,
  offsetX = 12,
  offsetY = 12,
}: FloatingPanelShellProps) => {
  const [position, setPosition] = useState({ right: 0, bottom: 0 });

  useLayoutEffect(() => {
    if (!open) {
      return;
    }

    const updatePosition = () => {
      const triggerRect = triggerRef?.current?.getBoundingClientRect();

      if (!triggerRect) {
        setPosition({ right: 24, bottom: 24 });
        return;
      }

      setPosition({
        right: Math.max(0, window.innerWidth - triggerRect.right + offsetX),
        bottom: Math.max(0, window.innerHeight - triggerRect.bottom + offsetY),
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, offsetX, offsetY, triggerRef]);

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.button
            type="button"
            aria-label={`Fermer ${title}`}
            className="fixed inset-0 z-[11990] cursor-default bg-transparent"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            onClick={() => onOpenChange(false)}
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            className="fixed z-[12000] overflow-hidden rounded-2xl"
            style={{
              right: `${position.right}px`,
              bottom: `${position.bottom}px`,
              width: `${panelWidthPx}px`,
              height: `${panelHeightPx}px`,
              transformOrigin: "bottom right",
            }}
            initial={{ opacity: 0, x: 28, y: 28, scale: 0.92 }}
            animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
            exit={{ opacity: 0, x: 28, y: 28, scale: 0.92 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
          >
            {children}

            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="absolute right-3 top-3 inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              aria-label="Fermer l'aide"
            >
              <X className="h-4 w-4 stroke-[1.8]" />
            </button>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
};
